import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { BlockAuthor, Delete, Exclude, MuteAuthor, ToContacted, ToCRM, ToPodiumTitle,
  Watch } from '../../../composed-components/Actions';
import { BASEFONTSIZE, COLUMNSHEADING, PAGESIZE, REFETCHDELAY, RefetchOptions, SOURCE_FORMAT } from '../constants';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import ResizeTable from '../../../composed-components/Resize';
import { EFontSize, TColumns } from '../types/types';
import Pagination from '../../../components/Pagination';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import { useUserPermit } from '../../../context/User.context';
import { useActions } from '../../../context/Actions.context';
import Legend from '../../../composed-components/Legend';
import { TableFooter } from '../../../components/Table';
import getAllColumns from '../columns/getAllColumns';
import getDataSource from '../utils/getDataSource';
import { POPULAR, POPULAR_AO3, SOURCE } from '../../../core/constants';

const Popular = () => {
  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('All Titles');

  const { edit_titles, move_titles_hubspot } = useUserPermit();
  const { source: sourcePath, isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { filter } = useFilter();
  const { sort } = useSort();

  const changePage = p => setPage(p);
  const deleteBooks = book => setDeleteItems(book);
  const changeFontSize = size => setFontSize(size);

  const SOURCE_TO_POPULAR: Record<string, string> = {
    [SOURCE['royal-road']]: POPULAR,
    [SOURCE.ao3]: POPULAR_AO3
  };

  const TAB_ID = SOURCE_TO_POPULAR[sourcePath['source']] ?? POPULAR;

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    TAB_ID, {
      page,
      ...filter[TAB_ID],
      ...sort[TAB_ID]
    });

  const { count, refetch: refetchRowCount } = useDataRowCount(TAB_ID, { ...filter[TAB_ID] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortComponent = useCallback(
    (title: string) => (
      <Sorting id={TAB_ID} title={title} changePage={changePage} />
    ),
    []
  );

  const thead = useMemo(() => {
    const allColumns = getAllColumns(sortComponent);
    const head = COLUMNSHEADING[TAB_ID].map(title => allColumns[title]);

    if (edit_titles) {
      head.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            {
              TAB_ID === POPULAR_AO3 &&
              <>
                <ToContacted book={book} id={TAB_ID} />
                <ToPodiumTitle book={book} id={TAB_ID} />
                <MuteAuthor book={book} id={TAB_ID} />
              </>
            }
            <Delete book={book} id={TAB_ID} />
            <BlockAuthor book={book} id={TAB_ID} />
            <Exclude book={book} id={TAB_ID} />
            <Watch book={book} id={TAB_ID} />
            {move_titles_hubspot && <ToCRM book={book} id={TAB_ID} />}
          </div>
        )
      });
    }

    return head;
  }, [edit_titles, move_titles_hubspot, sortComponent]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data, selectedOption, SOURCE_FORMAT[sourcePath['source']]);

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }

    return () => {
      setSelectedOption('All Titles');
    };
  }, [page, filter[TAB_ID], sort[TAB_ID]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm
          id={TAB_ID}
          page={page}
          changePage={changePage}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
        <Settings
          thead={thead}
          id={TAB_ID}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteBooks={deleteBooks}
          deleteItems={deleteItems}
        />
      </div>
      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            id={TAB_ID}
            dataSource={dataSource}
            deleteItems={deleteItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />

          <TableFooter onlyPagination={TAB_ID === POPULAR_AO3}>
            { TAB_ID !== POPULAR_AO3 &&
            <Legend map={['crm', 'podium']} />}
            <Pagination
              total={count}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>

        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default Popular;